import React, {CSSProperties, forwardRef, HTMLAttributes} from 'react'

import {ensureSurfaceStyles, getSurfaceStyle, highlightClass, highlightDuration, surfaceColors, surfaceSizes} from './styles.js'

interface CardProps extends HTMLAttributes<HTMLDivElement> {
    /** The selected background */
    selected?: boolean
    /** A shadow instead of a border */
    shadow?: boolean
}

/**
 * A white block with rounded corners and a border (or a shadow). It has a padding of 6 and grows (flex 1) unless
 * the style sets flex or width. Rest props and the ref go to the <div>.
 */
const Card = forwardRef<HTMLDivElement, CardProps>(function Card({style = {}, selected, shadow, onClick, children, ...rest}, ref) {
    const cardStyle: CSSProperties = {
        background: selected ? surfaceColors.selectedBackground : surfaceColors.background,
        flex: style.flex || style.width ? undefined : 1,
        padding: surfaceSizes.cardPadding,
        ...getSurfaceStyle({shadow}),
        borderRadius: surfaceSizes.cardRadius,
        flexShrink: 0,
        cursor: onClick ? 'pointer' : 'default',
        ...style
    }

    return (
        <div {...rest} ref={ref} onClick={onClick} style={cardStyle}>
            {children}
        </div>
    )
})

export default Card

/**
 * Draws attention to a Card (or any element) on the page by pulsing its background twice, for example after
 * scrolling it into view. Takes the element itself, so it works from outside the component that renders the Card.
 */
export function highlightCard(element: Element) {
    ensureSurfaceStyles()
    element.classList.add(highlightClass)
    setTimeout(() => element.classList.remove(highlightClass), highlightDuration)
}
